import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useOnClickOutside from "../../../hooks/useOnClickOutside";
import TextButton from "../../TextButton";
import IconButton from "../../IconButton";
import { ScenesMenuContainer, OptionsContainer, RelativeContainer, TextButtonLabel } from "./styles";
import Toggle from "../../Toggle";
import classNames from "classnames";

export default function ScenesMenu({ onClick, disabled, id, documentScenes, selected, onSelect, onToggle, toggled }) {
  const scenes = documentScenes?.value?.scenes?.content?.[0]?.content;
  const [showSubmenu, setShowSubmenu] = useState(false);
  const submenuControllerRef = useRef();

  const options = useMemo(() => {
    if (Array.isArray(scenes)) {
      return scenes.map((_, value) => ({
        label: `Scene ${value + 1}`,
        value,
      }));
    }

    return [];
  }, [scenes]);

  // Clamp selected index to valid range in case restartFromIndex is stale
  // (e.g., scenes were regenerated with fewer scenes than before)
  const clampedSelected = options.length > 0 ? Math.min(selected, options.length - 1) : selected;
  const selectedOption = options.length > 0 ? options[clampedSelected] : null;

  // Sync clamped value back to the store so restartFromIndex stays valid
  useEffect(() => {
    if (options.length > 0 && selected !== clampedSelected) {
      onSelect(clampedSelected);
    }
  }, [selected, clampedSelected, options.length, onSelect]);

  useOnClickOutside(submenuControllerRef, () => setShowSubmenu(false));

  const onSubmenuItemClick = (option) => {
    onSelect(option.value);
    setShowSubmenu(false);
  };

  return (
    <RelativeContainer
      ref={submenuControllerRef}
      id={id}
    >
      <ScenesMenuContainer className={disabled ? "disabled" : ""}>
        <TextButton
          onClick={onClick}
          disabled={disabled || !selectedOption}
          src="/assets/AI-sparkle.svg"
          size={24}
        >
          <TextButtonLabel>
            <p className="text-button-label">Generate Draft</p>
            <p className="text-button-small">{selectedOption?.label ?? "Select a Scene"}</p>
          </TextButtonLabel>
        </TextButton>
        <IconButton
          size={22}
          disabled={disabled}
          onClick={() => setShowSubmenu((prevState) => !prevState)}
          src="/assets/down-chevron.svg"
        />
      </ScenesMenuContainer>
      <OptionsContainer className={classNames({ hidden: !showSubmenu })}>
        <header>
          <div className="toggle">
            <Toggle
              checked={toggled}
              set={onToggle}
            />
          </div>
          <p>Continue where the text in the document leaves off</p>
        </header>
        <ul>
          {options.map((option, i) => (
            <li
              key={i}
              className="option-item"
            >
              <OptionButton
                onClick={onSubmenuItemClick}
                selected={option.value === selectedOption?.value}
                option={option}
              />
            </li>
          ))}
        </ul>
      </OptionsContainer>
    </RelativeContainer>
  );
}

const OptionButton = ({ onClick, option, selected }) => {
  const handleOnClick = useCallback(() => {
    onClick(option);
  }, [onClick, option]);

  return (
    <button
      className={selected ? "option-item-button selected" : "option-item-button"}
      onClick={handleOnClick}
    >
      {option.label}
    </button>
  );
};
